import { Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { formatNumber } from "../../../../../common/utils";
import {
	MeasurableValueDTO,
	ThirdPartyTripsPageDTO,
	ThirdPartyTripsPageItemDTO,
} from "../../../../../datasources/api-client/data-contracts";

// Hook to managa select state
type EmissionsKey = "co2Ttw" | "co2Wtw";

export const getRows = (data: ThirdPartyTripsPageDTO, t: TFunction) => {
	if (!data) return [];

	return data.items.map((item: ThirdPartyTripsPageItemDTO) => {
		const safeNumber = (val: any): number => {
			if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
				return 0;
			}
			return Number(val);
		};

		return {
			id: item.id,
			source: item.sourceLabel,
			startDate: dayjs(item.from).format("DD/MM/YYYY"),
			endDate: dayjs(item.to).format("DD/MM/YYYY"),
			methodology: item.methodologyLabel,
			isCertified: item.isCertified,
			distance: safeNumber(item.distance.value),
			weight: safeNumber(item.weight.value),
			transportType: item.transportTypeLabel ? item.transportTypeLabel : "-",
			tCo2eWtw: item.co2Wtw,
			tCo2eTtw: item.co2Ttw,
		};
	});
};

export const colsData = (t: TFunction): GridColDef[] => [
	{
		field: "id",
		headerName: "ID",
		hideable: true,
		flex: 0.7,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "source",
		headerName: "emission",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "startDate",
		headerName: "Start",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "endDate",
		headerName: "End",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "methodology",
		headerName: "methodology",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "isCertified",
		headerName: "isCertified",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value ? t("yes") : t("no")}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "distance",
		headerName: "distance",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="flex-end" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "weight",
		headerName: "weight",
		hideable: true,
		flex: 0.8,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "transportType",
		headerName: "transportType",
		hideable: true,
		flex: 1,
		renderCell: (params) => {
			return (
				<Stack height="100%" alignItems="start" justifyContent="center">
					<Typography variant="body2">{params.value}</Typography>
				</Stack>
			);
		},
	},
	{
		field: "tCo2eWtw",
		headerName: "emissions wtw",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Typography
				variant="body2"
				sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%" }}
			>
				{formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2)}
			</Typography>
		),
	},
	{
		field: "tCo2eTtw",
		headerName: "emissions ttw",
		hideable: true,
		flex: 1,
		renderCell: (params) => (
			<Typography
				variant="body2"
				sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%" }}
			>
				{formatNumber((params.value as MeasurableValueDTO).value, "decimal", 2, 2)}
			</Typography>
		),
	},
];

// These columns must be hidden when WTW is selected
const TTW_FIELDS = new Set(["tCo2eTtw", "energyTtw"]);
// These columns must be hidden when TTW is selected
const WTW_FIELDS = new Set(["tCo2eWtw", "energyWtw"]);

// Measure map for the columns
const UNIT_BY_FIELD: Record<string, string> = {
	distance: "km",
	weight: "t",
	tCo2eTtw: "tCO2e",
	tCo2eWtw: "tCO2e",
};

export const getColumns = (dataCol: GridColDef[], t: TFunction, headerTextColor: string, emissionsKey: EmissionsKey) =>
	dataCol
		.filter((col) => {
			// Filter emission columns based on the selected emissions key
			// co2Ttw → remove WTW columns
			// co2Wtw → remove TTW columns
			if (emissionsKey === "co2Ttw") {
				return !WTW_FIELDS.has(col.field);
			}
			if (emissionsKey === "co2Wtw") {
				return !TTW_FIELDS.has(col.field);
			}
			return true;
		})
		.map((col, index) => {
			const checkTransport = col.headerName === "transportType" ? "transport type" : col.headerName;

			const label = checkTransport ? t(`${checkTransport}`) : "";

			return {
				...col,
				headerName: label,
				renderHeader: () =>
					label && (
						<Stack direction="column" width="100%">
							<Stack
								justifyContent={index === 5 || index === 8 ? "right" : "start"}
								flexDirection="row"
								alignItems="baseline"
							>
								<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="body2">
									{label}
								</Typography>
								<Typography fontWeight={700} sx={{ color: headerTextColor }} variant="caption" ml={0.5}>
									{UNIT_BY_FIELD[col.field]}
								</Typography>
							</Stack>
						</Stack>
					),
			};
		});
